import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/club/member-management',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/club',
    component: Layout,
    redirect: '/club/member-management',
    name: 'Club',
    meta: {
      title: '俱乐部管理',
      icon: 'vi-ph:club-fill',
      alwaysShow: true
    },
    children: [
      {
        path: 'member-management',
        component: () => import('@/views/Club/MemberManagement/MemberManagement.vue'),
        name: 'MemberManagement',
        meta: {
          title: '成员管理'
        }
      },
      {
        path: 'swiper-management',
        component: () => import('@/views/Club/SwiperManagement/SwiperManagement.vue'),
        name: 'SwiperManagement',
        meta: {
          title: '轮播图管理'
        }
      },
      {
        path: 'add-swiper',
        component: () => import('@/views/Club/SwiperManagement/AddSwiper.vue'),
        name: 'AddSwiper',
        meta: {
          title: '添加轮播图',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/club/swiper-management'
        }
      },
      {
        path: 'edit-swiper',
        component: () => import('@/views/Club/SwiperManagement/EditSwiper.vue'),
        name: 'EditSwiper',
        meta: {
          title: '编辑轮播图',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/club/swiper-management'
        }
      },
      {
        path: 'club-info',
        component: () => import('@/views/Club/ClubInfo/ClubInfo.vue'),
        name: 'ClubInfo',
        meta: {
          title: '俱乐部信息'
        }
      },
      {
        path: 'role-management',
        component: () => import('@/views/Club/RoleManagement/RoleManagement.vue'),
        name: 'RoleManagement',
        meta: {
          title: '角色管理'
        }
      },
      {
        path: 'discovery-page-management',
        component: () => import('@/views/Club/DiscoveryPageManagement/DiscoveryPageManagement.vue'),
        name: 'DiscoveryPageManagement',
        meta: {
          title: '发现页管理'
        }
      }
    ]
  },
  {
    path: '/consultant',
    component: Layout,
    redirect: '/consultant/consultant-list',
    name: 'Consultant',
    meta: {
      title: '顾问管理',
      icon: 'vi-solar:user-bold',
      alwaysShow: true
    },
    children: [
      {
        path: 'consultant-list',
        component: () => import('@/views/Consultant/ConsultantList/ConsultantList.vue'),
        name: 'ConsultantList',
        meta: {
          title: '顾问列表'
        }
      },
      {
        path: 'add-consultant',
        component: () => import('@/views/Consultant/ConsultantList/AddConsultant.vue'),
        name: 'AddConsultant',
        meta: {
          title: '添加顾问',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/consultant/consultant-list'
        }
      },
      {
        path: 'edit-consultant',
        component: () => import('@/views/Consultant/ConsultantList/EditConsultant.vue'),
        name: 'EditConsultant',
        meta: {
          title: '编辑顾问',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/consultant/consultant-list'
        }
      },
      {
        path: 'service-category',
        component: () => import('@/views/Consultant/ServiceCategory/ServiceCategory.vue'),
        name: 'ServiceCategory',
        meta: {
          title: '服务分类'
        }
      }
    ]
  },
  {
    path: '/product',
    component: Layout,
    redirect: '/product/club-product',
    name: 'Product',
    meta: {
      title: '商品管理',
      icon: 'vi-material-symbols:inventory-2',
      alwaysShow: true
    },
    children: [
      {
        path: 'club-product',
        component: () => import('@/views/Product/ClubProduct/ClubProduct.vue'),
        name: 'ClubProduct',
        meta: {
          title: '俱乐部商品'
        }
      },
      {
        path: 'club-product/write',
        component: () => import('@/views/Product/ClubProduct/WritePage.vue'),
        name: 'ClubProductWrite',
        meta: {
          title: '商品编辑',
          hidden: true,
          noCache: true,
          activeMenu: '/product/club-product'
        }
      },
      {
        path: 'club-product/detail',
        component: () => import('@/views/Product/ClubProduct/Detail.vue'),
        name: 'ClubProductDetail',
        meta: {
          title: '商品详情',
          hidden: true
        }
      },
      {
        path: 'club-product/upload-cloud',
        component: () => import('@/views/Product/ClubProduct/UploadCloudPage.vue'),
        name: 'ClubProductUploadCloud',
        meta: {
          title: '上架云仓',
          hidden: true,
          noCache: true,
          activeMenu: '/product/club-product'
        }
      },
      {
        path: 'club-product/add-from-cloud',
        component: () => import('@/views/Product/ClubProduct/AddToClubPage.vue'),
        name: 'ClubProductAddFromCloud',
        meta: {
          title: '添加到俱乐部',
          hidden: true,
          noCache: true,
          activeMenu: '/product/cloud-product'
        }
      },
      {
        path: 'cloud-product',
        component: () => import('@/views/Product/CloudProduct/CloudProduct.vue'),
        name: 'CloudProduct',
        meta: {
          title: '云仓商品'
        }
      },
      {
        path: 'category',
        component: () => import('@/views/Product/Category/Category.vue'),
        name: 'ProductCategory',
        meta: {
          title: '商品分类'
        }
      }
    ]
  },
  {
    path: '/content',
    component: Layout,
    redirect: '/content/dynamic-management',
    name: 'Content',
    meta: {
      title: '内容管理',
      icon: 'vi-mingcute:content-ai-fill',
      alwaysShow: true
    },
    children: [
      {
        path: 'dynamic-management',
        component: () => import('@/views/Content/DynamicManagement/DynamicManagement.vue'),
        name: 'DynamicManagement',
        meta: {
          title: '动态管理'
        }
      },
      {
        path: 'dynamic-comment',
        component: () => import('@/views/Content/DynamicManagement/DynamicComment.vue'),
        name: 'DynamicComment',
        meta: {
          title: '评论管理',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/dynamic-management'
        }
      },
      {
        path: 'dynamic-comment-reply',
        component: () => import('@/views/Content/DynamicManagement/DynamicCommentReply.vue'),
        name: 'DynamicCommentReply',
        meta: {
          title: '回复管理',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/dynamic-management'
        }
      },
      {
        path: 'supply-management',
        component: () => import('@/views/Content/SupplyManagement/SupplyManagement.vue'),
        name: 'SupplyManagement',
        meta: {
          title: '供需管理'
        }
      },
      {
        path: 'note-management',
        component: () => import('@/views/Content/NoteManagement/NoteManagement.vue'),
        name: 'NoteManagement',
        meta: {
          title: '笔记管理'
        }
      },
      {
        path: 'add-note',
        component: () => import('@/views/Content/NoteManagement/AddNote.vue'),
        name: 'AddNote',
        meta: {
          title: '新建笔记',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/note-management'
        }
      },
      {
        path: 'edit-note',
        component: () => import('@/views/Content/NoteManagement/EditNote.vue'),
        name: 'EditNote',
        meta: {
          title: '编辑笔记',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/note-management'
        }
      },
      {
        path: 'activity-management',
        component: () => import('@/views/Content/ActivityManagement/ActivityManagement.vue'),
        name: 'ActivityManagement',
        meta: {
          title: '活动管理'
        }
      },
      {
        path: 'add-activity',
        component: () => import('@/views/Content/ActivityManagement/AddActivity.vue'),
        name: 'AddActivity',
        meta: {
          title: '新建活动',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/activity-management'
        }
      },
      {
        path: 'edit-activity',
        component: () => import('@/views/Content/ActivityManagement/EditActivity.vue'),
        name: 'EditActivity',
        meta: {
          title: '编辑活动',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/activity-management'
        }
      },
      {
        path: 'activity-detail',
        component: () => import('@/views/Content/ActivityManagement/ActivityDetail.vue'),
        name: 'DetailActivity',
        meta: {
          title: '活动详情',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/activity-management'
        }
      },
      {
        path: 'enrollment-detail',
        component: () => import('@/views/Content/ActivityManagement/EnrollmentDetail.vue'),
        name: 'EnrollmentDetail',
        meta: {
          title: '报名详情',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/content/activity-management'
        }
      }
    ]
  },
  {
    path: '/business',
    component: Layout,
    redirect: '/business/BusinessManagement',
    name: 'Business',
    meta: {
      title: '商机管理',
      icon: 'vi-mdi:business-outline',
      alwaysShow: true
    },
    children: [
      {
        path: 'clue-management',
        component: () => import('@/views/Business/ClueManagement/ClueManagement.vue'),
        name: 'ClueManagement',
        meta: {
          title: '线索管理'
        }
      },
      {
        path: 'clue-detail',
        component: () => import('@/views/Business/ClueManagement/ClueDetail.vue'),
        name: 'ClueDetail',
        meta: {
          title: '线索详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/business/clue-management'
        }
      },
      {
        path: 'clue-follow',
        component: () => import('@/views/Business/ClueFollow/ClueFollow.vue'),
        name: 'ClueFollow',
        meta: {
          title: '线索跟进',
          noCache: true
        }
      },
      {
        path: 'clue-follow-detail',
        component: () => import('@/views/Business/ClueFollow/ClueFollowDetail.vue'),
        name: 'ClueFollowDetail',
        meta: {
          title: '线索跟进详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/business/clue-follow'
        }
      },
      {
        path: 'business-management',
        component: () => import('@/views/Business/BusinessManagement/BusinessManagement.vue'),
        name: 'BusinessManagement',
        meta: {
          title: '商机管理'
        }
      },
      {
        path: 'business-detail',
        component: () => import('@/views/Business/BusinessManagement/BusinessDetail.vue'),
        name: 'BusinessDetail',
        meta: {
          title: '商机详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/business/business-management'
        }
      },
      {
        path: 'business-follow',
        component: () => import('@/views/Business/BusinessFollow/BusinessFollow.vue'),
        name: 'BusinessFollow',
        meta: {
          title: '商机跟进',
          noCache: true
        }
      },
      {
        path: 'business-follow-detail',
        component: () => import('@/views/Business/BusinessFollow/BusinessFollowDetail.vue'),
        name: 'BusinessFollowDetail',
        meta: {
          title: '商机跟进详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/business/business-follow'
        }
      }
    ]
  },
  {
    path: '/order',
    component: Layout,
    redirect: '/order/goods-order',
    name: 'Order',
    meta: {
      title: '订单管理',
      icon: 'vi-mdi:order-bool-ascending-variant',
      alwaysShow: true
    },
    children: [
      {
        path: 'goods-order',
        component: () => import('@/views/Order/GoodsOrder/GoodsOrder.vue'),
        name: 'GoodsOrder',
        meta: {
          title: '商品订单'
        }
      },
      {
        path: 'goods-order-detail',
        component: () => import('@/views/Order/GoodsOrder/GoodsOrderDetail.vue'),
        name: 'GoodsOrderDetail',
        meta: {
          title: '商品订单详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/order/goods-order'
        }
      },
      {
        path: 'activity-order',
        component: () => import('@/views/Order/ActivityOrder/ActivityOrder.vue'),
        name: 'ActivityOrder',
        meta: {
          title: '活动订单'
        }
      },
      {
        path: 'activity-order-detail',
        component: () => import('@/views/Order/ActivityOrder/ActivityOrderDetail.vue'),
        name: 'ActivityOrderDetail',
        meta: {
          title: '活动订单详情',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/order/activity-order'
        }
      }
    ]
  },
  {
    path: '/membership',
    component: Layout,
    redirect: '/membership/membership-management',
    name: 'Member',
    meta: {
      title: '俱乐部会员',
      icon: 'vi-tdesign:user-vip',
      alwaysShow: true
    },
    children: [
      {
        path: 'membership-list',
        component: () => import('@/views/Membership/MembershipList/MembershipList.vue'),
        name: 'MembershipList',
        meta: {
          title: '会员列表'
        }
      },
      {
        path: 'member-info/:uuid',
        component: () => import('@/views/Membership/MembershipList/MembershipInfo.vue'),
        name: 'MembershipInfo',
        meta: {
          title: '会员信息',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/membership/membership-list'
        }
      },
      {
        path: 'audit-list',
        component: () => import('@/views/Membership/AuditList/AuditList.vue'),
        name: 'AuditList',
        meta: {
          title: '审核列表'
        }
      },
      {
        path: 'member-base',
        component: () => import('@/views/Membership/AuditList/MembershipBaseInfo.vue'),
        name: 'MembershipBaseInfo',
        meta: {
          title: '会员信息',
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/membership/audit-list'
        }
      },
      {
        path: 'member-tag-management',
        component: () => import('@/views/Membership/MembertagList/MemberTagManagement.vue'),
        name: 'MemberTagManagement',
        meta: {
          title: '会员标签',
          canTo: true
        }
      }
    ]
  },
  {
    path: '/contract',
    component: Layout,
    redirect: '/contract/contract-list',
    name: 'Contract',
    meta: {
      title: '合同管理',
      icon: 'vi-material-symbols-light:contract-outline',
      alwaysShow: true
    },
    children: [
      {
        path: 'contract-list',
        component: () => import('@/views/Contract/ContractList/ContractList.vue'), // 合同列表组件路径
        name: 'ContractList',
        meta: {
          title: '合同列表'
        }
      }
    ]
  }
  // ,
  // {
  //   path: '/dashboard',
  //   component: Layout,
  //   redirect: '/dashboard/analysis',
  //   name: 'Dashboard',
  //   meta: {
  //     title: t('router.dashboard'),
  //     icon: 'vi-ant-design:dashboard-filled',
  //     alwaysShow: true
  //   },
  //   children: [
  //     {
  //       path: 'analysis',
  //       component: () => import('@/views/Dashboard/Analysis.vue'),
  //       name: 'Analysis',
  //       meta: {
  //         title: t('router.analysis'),
  //         noCache: true,
  //         affix: true
  //       }
  //     },
  //     {
  //       path: 'workplace',
  //       component: () => import('@/views/Dashboard/Workplace.vue'),
  //       name: 'Workplace',
  //       meta: {
  //         title: t('router.workplace'),
  //         noCache: true
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/external-link',
  //   component: Layout,
  //   meta: {},
  //   name: 'ExternalLink',
  //   children: [
  //     {
  //       path: 'https://element-plus-admin-doc.cn/',
  //       name: 'DocumentLink',
  //       meta: {
  //         title: t('router.document'),
  //         icon: 'vi-clarity:document-solid'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/guide',
  //   component: Layout,
  //   name: 'Guide',
  //   meta: {},
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import('@/views/Guide/Guide.vue'),
  //       name: 'GuideDemo',
  //       meta: {
  //         title: t('router.guide'),
  //         icon: 'vi-cib:telegram-plane'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/components',
  //   component: Layout,
  //   name: 'ComponentsDemo',
  //   meta: {
  //     title: t('router.component'),
  //     icon: 'vi-bx:bxs-component',
  //     alwaysShow: true
  //   },
  //   children: [
  //     {
  //       path: 'form',
  //       component: getParentLayout(),
  //       redirect: '/components/form/default-form',
  //       name: 'Form',
  //       meta: {
  //         title: t('router.form'),
  //         alwaysShow: true
  //       },
  //       children: [
  //         {
  //           path: 'default-form',
  //           component: () => import('@/views/Components/Form/DefaultForm.vue'),
  //           name: 'DefaultForm',
  //           meta: {
  //             title: t('router.defaultForm')
  //           }
  //         },
  //         {
  //           path: 'use-form',
  //           component: () => import('@/views/Components/Form/UseFormDemo.vue'),
  //           name: 'UseForm',
  //           meta: {
  //             title: 'UseForm'
  //           }
  //         }
  //       ]
  //     },
  //     {
  //       path: 'table',
  //       component: getParentLayout(),
  //       redirect: '/components/table/default-table',
  //       name: 'TableDemo',
  //       meta: {
  //         title: t('router.table'),
  //         alwaysShow: true
  //       },
  //       children: [
  //         {
  //           path: 'default-table',
  //           component: () => import('@/views/Components/Table/DefaultTable.vue'),
  //           name: 'DefaultTable',
  //           meta: {
  //             title: t('router.defaultTable')
  //           }
  //         },
  //         {
  //           path: 'use-table',
  //           component: () => import('@/views/Components/Table/UseTableDemo.vue'),
  //           name: 'UseTable',
  //           meta: {
  //             title: 'UseTable'
  //           }
  //         },
  //         {
  //           path: 'tree-table',
  //           component: () => import('@/views/Components/Table/TreeTable.vue'),
  //           name: 'TreeTable',
  //           meta: {
  //             title: t('router.treeTable')
  //           }
  //         },
  //         {
  //           path: 'table-image-preview',
  //           component: () => import('@/views/Components/Table/TableImagePreview.vue'),
  //           name: 'TableImagePreview',
  //           meta: {
  //             title: t('router.PicturePreview')
  //           }
  //         },
  //         {
  //           path: 'table-video-preview',
  //           component: () => import('@/views/Components/Table/TableVideoPreview.vue'),
  //           name: 'TableVideoPreview',
  //           meta: {
  //             title: t('router.tableVideoPreview')
  //           }
  //         },
  //         {
  //           path: 'card-table',
  //           component: () => import('@/views/Components/Table/CardTable.vue'),
  //           name: 'CardTable',
  //           meta: {
  //             title: t('router.cardTable')
  //           }
  //         }
  //       ]
  //     },
  //     {
  //       path: 'editor-demo',
  //       component: getParentLayout(),
  //       redirect: '/components/editor-demo/editor',
  //       name: 'EditorDemo',
  //       meta: {
  //         title: t('router.editor'),
  //         alwaysShow: true
  //       },
  //       children: [
  //         {
  //           path: 'editor',
  //           component: () => import('@/views/Components/Editor/Editor.vue'),
  //           name: 'Editor',
  //           meta: {
  //             title: t('router.richText')
  //           }
  //         },
  //         {
  //           path: 'json-editor',
  //           component: () => import('@/views/Components/Editor/JsonEditor.vue'),
  //           name: 'JsonEditor',
  //           meta: {
  //             title: t('router.jsonEditor')
  //           }
  //         }
  //       ]
  //     },
  //     {
  //       path: 'search',
  //       component: () => import('@/views/Components/Search.vue'),
  //       name: 'Search',
  //       meta: {
  //         title: t('router.search')
  //       }
  //     },
  //     {
  //       path: 'descriptions',
  //       component: () => import('@/views/Components/Descriptions.vue'),
  //       name: 'Descriptions',
  //       meta: {
  //         title: t('router.descriptions')
  //       }
  //     },
  //     {
  //       path: 'image-viewer',
  //       component: () => import('@/views/Components/ImageViewer.vue'),
  //       name: 'ImageViewer',
  //       meta: {
  //         title: t('router.imageViewer')
  //       }
  //     },
  //     {
  //       path: 'dialog',
  //       component: () => import('@/views/Components/Dialog.vue'),
  //       name: 'Dialog',
  //       meta: {
  //         title: t('router.dialog')
  //       }
  //     },
  //     {
  //       path: 'icon',
  //       component: () => import('@/views/Components/Icon.vue'),
  //       name: 'Icon',
  //       meta: {
  //         title: t('router.icon')
  //       }
  //     },
  //     {
  //       path: 'icon-picker',
  //       component: () => import('@/views/Components/IconPicker.vue'),
  //       name: 'IconPicker',
  //       meta: {
  //         title: t('router.iconPicker')
  //       }
  //     },
  //     {
  //       path: 'echart',
  //       component: () => import('@/views/Components/Echart.vue'),
  //       name: 'Echart',
  //       meta: {
  //         title: t('router.echart')
  //       }
  //     },
  //     {
  //       path: 'count-to',
  //       component: () => import('@/views/Components/CountTo.vue'),
  //       name: 'CountTo',
  //       meta: {
  //         title: t('router.countTo')
  //       }
  //     },
  //     {
  //       path: 'qrcode',
  //       component: () => import('@/views/Components/Qrcode.vue'),
  //       name: 'Qrcode',
  //       meta: {
  //         title: t('router.qrcode')
  //       }
  //     },
  //     {
  //       path: 'highlight',
  //       component: () => import('@/views/Components/Highlight.vue'),
  //       name: 'Highlight',
  //       meta: {
  //         title: t('router.highlight')
  //       }
  //     },
  //     {
  //       path: 'infotip',
  //       component: () => import('@/views/Components/Infotip.vue'),
  //       name: 'Infotip',
  //       meta: {
  //         title: t('router.infotip')
  //       }
  //     },
  //     {
  //       path: 'input-password',
  //       component: () => import('@/views/Components/InputPassword.vue'),
  //       name: 'InputPassword',
  //       meta: {
  //         title: t('router.inputPassword')
  //       }
  //     },
  //     {
  //       path: 'waterfall',
  //       component: () => import('@/views/Components/Waterfall.vue'),
  //       name: 'waterfall',
  //       meta: {
  //         title: t('router.waterfall')
  //       }
  //     },
  //     {
  //       path: 'image-cropping',
  //       component: () => import('@/views/Components/ImageCropping.vue'),
  //       name: 'ImageCropping',
  //       meta: {
  //         title: t('router.imageCropping')
  //       }
  //     },
  //     {
  //       path: 'video-player',
  //       component: () => import('@/views/Components/VideoPlayer.vue'),
  //       name: 'VideoPlayer',
  //       meta: {
  //         title: t('router.videoPlayer')
  //       }
  //     },
  //     {
  //       path: 'avatars',
  //       component: () => import('@/views/Components/Avatars.vue'),
  //       name: 'Avatars',
  //       meta: {
  //         title: t('router.avatars')
  //       }
  //     },
  //     {
  //       path: 'i-agree',
  //       component: () => import('@/views/Components/IAgree.vue'),
  //       name: 'IAgree',
  //       meta: {
  //         title: t('router.iAgree')
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/function',
  //   component: Layout,
  //   redirect: '/function/multipleTabs',
  //   name: 'Function',
  //   meta: {
  //     title: t('router.function'),
  //     icon: 'vi-ri:function-fill',
  //     alwaysShow: true
  //   },
  //   children: [
  //     {
  //       path: 'multiple-tabs',
  //       component: () => import('@/views/Function/MultipleTabs.vue'),
  //       name: 'MultipleTabs',
  //       meta: {
  //         title: t('router.multipleTabs')
  //       }
  //     },
  //     {
  //       path: 'multiple-tabs-demo/:id',
  //       component: () => import('@/views/Function/MultipleTabsDemo.vue'),
  //       name: 'MultipleTabsDemo',
  //       meta: {
  //         hidden: true,
  //         title: t('router.details'),
  //         canTo: true,
  //         activeMenu: '/function/multiple-tabs'
  //       }
  //     },
  //     {
  //       path: 'request',
  //       component: () => import('@/views/Function/Request.vue'),
  //       name: 'Request',
  //       meta: {
  //         title: t('router.request')
  //       }
  //     },
  //     {
  //       path: 'test',
  //       component: () => import('@/views/Function/Test.vue'),
  //       name: 'Test',
  //       meta: {
  //         title: t('router.permission'),
  //         permission: ['add', 'edit', 'delete']
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/hooks',
  //   component: Layout,
  //   redirect: '/hooks/useWatermark',
  //   name: 'Hooks',
  //   meta: {
  //     title: 'hooks',
  //     icon: 'vi-ic:outline-webhook',
  //     alwaysShow: true
  //   },
  //   children: [
  //     {
  //       path: 'useWatermark',
  //       component: () => import('@/views/hooks/useWatermark.vue'),
  //       name: 'UseWatermark',
  //       meta: {
  //         title: 'useWatermark'
  //       }
  //     },
  //     {
  //       path: 'useTagsView',
  //       component: () => import('@/views/hooks/useTagsView.vue'),
  //       name: 'UseTagsView',
  //       meta: {
  //         title: 'useTagsView'
  //       }
  //     },
  //     {
  //       path: 'useValidator',
  //       component: () => import('@/views/hooks/useValidator.vue'),
  //       name: 'UseValidator',
  //       meta: {
  //         title: 'useValidator'
  //       }
  //     },
  //     {
  //       path: 'useCrudSchemas',
  //       component: () => import('@/views/hooks/useCrudSchemas.vue'),
  //       name: 'UseCrudSchemas',
  //       meta: {
  //         title: 'useCrudSchemas'
  //       }
  //     },
  //     {
  //       path: 'useClipboard',
  //       component: () => import('@/views/hooks/useClipboard.vue'),
  //       name: 'UseClipboard',
  //       meta: {
  //         title: 'useClipboard'
  //       }
  //     },
  //     {
  //       path: 'useNetwork',
  //       component: () => import('@/views/hooks/useNetwork.vue'),
  //       name: 'UseNetwork',
  //       meta: {
  //         title: 'useNetwork'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/level',
  //   component: Layout,
  //   redirect: '/level/menu1/menu1-1/menu1-1-1',
  //   name: 'Level',
  //   meta: {
  //     title: t('router.level'),
  //     icon: 'vi-carbon:skill-level-advanced'
  //   },
  //   children: [
  //     {
  //       path: 'menu1',
  //       name: 'Menu1',
  //       component: getParentLayout(),
  //       redirect: '/level/menu1/menu1-1/menu1-1-1',
  //       meta: {
  //         title: t('router.menu1')
  //       },
  //       children: [
  //         {
  //           path: 'menu1-1',
  //           name: 'Menu11',
  //           component: getParentLayout(),
  //           redirect: '/level/menu1/menu1-1/menu1-1-1',
  //           meta: {
  //             title: t('router.menu11'),
  //             alwaysShow: true
  //           },
  //           children: [
  //             {
  //               path: 'menu1-1-1',
  //               name: 'Menu111',
  //               component: () => import('@/views/Level/Menu111.vue'),
  //               meta: {
  //                 title: t('router.menu111')
  //               }
  //             }
  //           ]
  //         },
  //         {
  //           path: 'menu1-2',
  //           name: 'Menu12',
  //           component: () => import('@/views/Level/Menu12.vue'),
  //           meta: {
  //             title: t('router.menu12')
  //           }
  //         }
  //       ]
  //     },
  //     {
  //       path: 'menu2',
  //       name: 'Menu2',
  //       component: () => import('@/views/Level/Menu2.vue'),
  //       meta: {
  //         title: t('router.menu2')
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/example',
  //   component: Layout,
  //   redirect: '/example/example-dialog',
  //   name: 'Example',
  //   meta: {
  //     title: t('router.example'),
  //     icon: 'vi-ep:management',
  //     alwaysShow: true
  //   },
  //   children: [
  //     {
  //       path: 'example-dialog',
  //       component: () => import('@/views/Example/Dialog/ExampleDialog.vue'),
  //       name: 'ExampleDialog',
  //       meta: {
  //         title: t('router.exampleDialog')
  //       }
  //     },
  //     {
  //       path: 'example-page',
  //       component: () => import('@/views/Example/Page/ExamplePage.vue'),
  //       name: 'ExamplePage',
  //       meta: {
  //         title: t('router.examplePage')
  //       }
  //     },
  //     {
  //       path: 'example-add',
  //       component: () => import('@/views/Example/Page/ExampleAdd.vue'),
  //       name: 'ExampleAdd',
  //       meta: {
  //         title: t('router.exampleAdd'),
  //         noTagsView: true,
  //         noCache: true,
  //         hidden: true,
  //         canTo: true,
  //         activeMenu: '/example/example-page'
  //       }
  //     },
  //     {
  //       path: 'example-edit',
  //       component: () => import('@/views/Example/Page/ExampleEdit.vue'),
  //       name: 'ExampleEdit',
  //       meta: {
  //         title: t('router.exampleEdit'),
  //         noTagsView: true,
  //         noCache: true,
  //         hidden: true,
  //         canTo: true,
  //         activeMenu: '/example/example-page'
  //       }
  //     },
  //     {
  //       path: 'example-detail',
  //       component: () => import('@/views/Example/Page/ExampleDetail.vue'),
  //       name: 'ExampleDetail',
  //       meta: {
  //         title: t('router.exampleDetail'),
  //         noTagsView: true,
  //         noCache: true,
  //         hidden: true,
  //         canTo: true,
  //         activeMenu: '/example/example-page'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/error',
  //   component: Layout,
  //   redirect: '/error/404',
  //   name: 'Error',
  //   meta: {
  //     title: t('router.errorPage'),
  //     icon: 'vi-ci:error',
  //     alwaysShow: true
  //   },
  //   children: [
  //     {
  //       path: '404-demo',
  //       component: () => import('@/views/Error/404.vue'),
  //       name: '404Demo',
  //       meta: {
  //         title: '404'
  //       }
  //     },
  //     {
  //       path: '403-demo',
  //       component: () => import('@/views/Error/403.vue'),
  //       name: '403Demo',
  //       meta: {
  //         title: '403'
  //       }
  //     },
  //     {
  //       path: '500-demo',
  //       component: () => import('@/views/Error/500.vue'),
  //       name: '500Demo',
  //       meta: {
  //         title: '500'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/authorization',
  //   component: Layout,
  //   redirect: '/authorization/user',
  //   name: 'Authorization',
  //   meta: {
  //     title: t('router.authorization'),
  //     icon: 'vi-eos-icons:role-binding',
  //     alwaysShow: true
  //   },
  //   children: [
  //     {
  //       path: 'department',
  //       component: () => import('@/views/Authorization/Department/Department.vue'),
  //       name: 'Department',
  //       meta: {
  //         title: t('router.department')
  //       }
  //     },
  //     {
  //       path: 'user',
  //       component: () => import('@/views/Authorization/User/User.vue'),
  //       name: 'User',
  //       meta: {
  //         title: t('router.user')
  //       }
  //     },
  //     {
  //       path: 'menu',
  //       component: () => import('@/views/Authorization/Menu/Menu.vue'),
  //       name: 'Menu',
  //       meta: {
  //         title: t('router.menuManagement')
  //       }
  //     },
  //     {
  //       path: 'role',
  //       component: () => import('@/views/Authorization/Role/Role.vue'),
  //       name: 'Role',
  //       meta: {
  //         title: t('router.role')
  //       }
  //     }
  //   ]
  // }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
